import { api } from './client';

export type AuthUser = {
  id: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  name?: string; // Computed full name from backend
  display_name?: string;
  avatar_icon?: string;
  avatar_color?: string;
  role?: string;
  is_admin?: boolean;
  is_premium?: boolean;
  subscription_status?: 'active' | 'canceled' | 'inactive' | 'past_due' | null;
  subscription_end_date?: string | null; // ISO timestamp
  current_period_end?: string | null; // ISO timestamp for next billing date
  unlocks_remaining?: number | null;
  calculations_remaining?: number | null;
  [key: string]: any;
};

type TokenResponse = {
  token?: string;
  jwt?: string;
  access_token?: string;
  [key: string]: any;
};

const extractToken = (data: TokenResponse): string | null => {
  return data?.token ?? data?.jwt ?? data?.access_token ?? null;
};

export async function signup(params: {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation?: string;
  accepted_terms?: boolean;
}): Promise<{ message: string; email: string; raw: any }> {
  const res = await api.post('/signup', { user: params });
  // Backend now returns a message instead of a token
  return { 
    message: res.data.message || 'Account created! Check your email to confirm and log in',
    email: params.email,
    raw: res.data 
  };
}

export async function login(params: {
  email: string;
  password: string;
}): Promise<{ token: string; user?: AuthUser; raw: any }> {
  const res = await api.post('/login', params);
  const token = extractToken(res.data);
  if (!token) throw new Error('Error logging in, try again later');
// if (!token) throw new Error('Login failed: No authentication token received from server.');

  return { token, user: res.data?.user, raw: res.data };
}

export async function logout(): Promise<void> {
  await api.delete('/logout');
}

export async function me(token?: string): Promise<AuthUser> {
  const config = token ? { headers: { Authorization: token } } : undefined;
  const res = await api.get('/me', config);
  // Backend returns { user: {...} }
  return res.data.user || res.data;
}

export async function resendConfirmation(email: string): Promise<{ message: string }> {
  const res = await api.post('/resend-confirmation', { email });
  return res.data;
}

export async function confirmEmail(token: string): Promise<{ token: string; user?: AuthUser }> {
  const res = await api.get(`/users/confirmation?confirmation_token=${token}`);
  const authToken = extractToken(res.data);
  if (!authToken) throw new Error('Confirmation failed: No authentication token received.');
  return { token: authToken, user: res.data?.user };
}

export async function deleteAccount(): Promise<{ message: string }> {
  const res = await api.delete('/delete-account');
  return res.data;
}
